import { DecoratorNode, LexicalEditor, LexicalNode, NodeKey } from 'lexical';
import { TemplatePresetPlaceholder } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/create-placeholders/template-preset-placeholder';
import { PlaceholderNode } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/shared';
import { ContactTextPlaceholderKey } from 'src/components/pages/conversations/components/general/text-placeholders-helpers';

export type SerializedCreateTemplatePresetPlaceholderNode = {
  presetKey: ContactTextPlaceholderKey;
  type: 'template-preset-placeholder';
  version: 1;
  fallbackValue?: string;
};

export const $createTemplatePresetPlaceholderNode = (
  presetKey: ContactTextPlaceholderKey,
  fallbackValue?: string,
): CreateTemplatePresetPlaceholderNode => {
  return new CreateTemplatePresetPlaceholderNode({ presetKey, fallbackValue });
};

export class CreateTemplatePresetPlaceholderNode extends DecoratorNode<JSX.Element> {
  __presetKey: ContactTextPlaceholderKey;
  __fallbackValue?: string;

  constructor(
    props: {
      presetKey: ContactTextPlaceholderKey;
      fallbackValue?: string;
    },
    key?: NodeKey,
  ) {
    super(key);

    this.__presetKey = props.presetKey;
    this.__fallbackValue = props.fallbackValue;
  }

  static getType(): string {
    return 'template-preset-placeholder';
  }

  static clone(
    node: CreateTemplatePresetPlaceholderNode,
  ): CreateTemplatePresetPlaceholderNode {
    return new CreateTemplatePresetPlaceholderNode(
      {
        presetKey: node.presetKey,
        fallbackValue: node.fallbackValue,
      },
      node.__key,
    );
  }

  get presetKey() {
    return this.__presetKey;
  }

  get fallbackValue() {
    return this.__fallbackValue;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');

    return span;
  }

  updateDOM(): boolean {
    return true;
  }

  getTextContent() {
    return `{{${this.presetKey}:${this.getKey()}}}`;
  }

  getDisplayTextContent() {
    return `{{${this.presetKey}}}`;
  }

  isKeyboardSelectable(): boolean {
    return false;
  }

  decorate(editor: LexicalEditor) {
    return (
      <TemplatePresetPlaceholder
        fallbackValue={this.fallbackValue}
        onSubmit={(newValue) => {
          editor.update(() => {
            const writable = this.getWritable();
            writable.__fallbackValue = newValue;
          });
        }}
        onRemove={() => {
          editor.update(() => {
            const writable = this.getWritable();
            writable.remove();
          });
        }}
      >
        <PlaceholderNode
          data-lgg-id="create-preset-placeholder-node"
          data-preset-key={this.presetKey}
          data-lexical-key={this.getKey()}
        >
          {this.getDisplayTextContent()}
        </PlaceholderNode>
      </TemplatePresetPlaceholder>
    );
  }

  static importJSON(
    serializedNode: SerializedCreateTemplatePresetPlaceholderNode,
  ): LexicalNode {
    return new CreateTemplatePresetPlaceholderNode({
      presetKey: serializedNode.presetKey,
      fallbackValue: serializedNode.fallbackValue,
    });
  }

  exportJSON(): SerializedCreateTemplatePresetPlaceholderNode {
    return {
      ...super.exportJSON(),
      type: 'template-preset-placeholder',
      presetKey: this.presetKey,
      fallbackValue: this.fallbackValue ?? undefined,
      version: 1,
    };
  }
}
